import { observer, useLocalObservable } from 'mobx-react';
import { runInAction } from 'mobx';
import { useEffect } from 'react';
import { useStore } from '../../../../../store/store';
import { cancelRequest } from '../../../../../api/api';
import ProfilerNameSearchInput from './ProfilerNameSearchInput';
import ProfilerNameSearchResults from './ProfilerNameSearchResults';

/**
 * ProfilerNameSearch - Generic name-based search component
 *
 * Combines search input + results list with integrated state management.
 * Works for any entity type (companies, individuals, etc.) via composition.
 *
 * @param {Object} apiEndpoint - API endpoint object from API_ENDPOINTS
 * @param {React.Component} ResultItemComponent - Component to render each result item
 * @param {Object} resultItemProps - Additional props passed to ResultItemComponent
 * @param {string} placeholder - Input placeholder text
 * @param {string} label - Input label text
 * @param {string} inputId - Unique ID for the input element
 * @param {boolean} disabled - Whether search is disabled
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 350)
 * @param {number} minSearchLength - Minimum characters to trigger search (default: 3)
 * @param {boolean} showSearchButton - Whether to show a manual search button (default: false)
 * @param {boolean} disableAutoSearch - Whether to disable auto-search on typing (default: false)
 * @param {string} initialSearchKey - TD-1709: Initial search key for URL restoration
 * @param {function} onSearchKeyChange - TD-1709: Callback when search key changes (for tracking)
 */
const ProfilerNameSearch = observer(
  ({
    apiEndpoint,
    ResultItemComponent,
    resultItemProps = {},
    placeholder = 'Type name here',
    label = 'Name',
    inputId = 'ps-input-searchKey',
    disabled = false,
    debounceMs = 350,
    minSearchLength = 3,
    showSearchButton = false,
    disableAutoSearch = false,
    initialSearchKey = '',
    onSearchKeyChange
  }) => {
    const { makeRequest } = useStore();

    const state = useLocalObservable(() => ({
      isTyping: false,
      searchKey: initialSearchKey || '',
      searchKeyResults: [],
      isLoading: false,
      loadError: null,
      inputTimeout: null,

      // Execute search immediately (no debounce)
      executeSearch: () => {
        const value = state.searchKey;

        // Clear any pending timeout
        if (state.inputTimeout) {
          clearTimeout(state.inputTimeout);
          state.inputTimeout = null;
        }

        // Don't search if too short
        if (value.trim().length < minSearchLength) {
          return;
        }

        runInAction(() => {
          state.isTyping = false;
          state.isLoading = true;
          state.loadError = null;
        });

        cancelRequest(apiEndpoint);
        makeRequest({
          endpoint: apiEndpoint,
          body: { searchKey: value.trim() },
          onSuccess: (response) => {
            runInAction(() => {
              state.searchKeyResults = response || [];
              state.isLoading = false;
            });
          },
          onError: (error) => {
            runInAction(() => {
              state.loadError = error?.message || 'Failed to load results';
              state.searchKeyResults = [];
              state.isLoading = false;
            });
          },
          onFinally: () => {
            runInAction(() => {
              state.isLoading = false;
            });
          }
        });
      },

      setSearchKey: (value) => {
        runInAction(() => {
          state.searchKey = value;
          state.isTyping = true;

          cancelRequest(apiEndpoint);
          state.searchKeyResults = [];

          // TD-1709: Notify parent of search key change for URL tracking
          onSearchKeyChange?.(value);

          // Clear previous timeout
          if (state.inputTimeout) {
            clearTimeout(state.inputTimeout);
          }

          // Clear results if search key is too short
          if (value.trim().length < minSearchLength) {
            state.searchKeyResults = [];
            state.isLoading = false;
            state.loadError = null;
            state.isTyping = false;
            return;
          }

          // Skip auto-search if disabled (button mode)
          if (disableAutoSearch) {
            state.isTyping = false;
            return;
          }

          // Set new timeout for debounced search
          state.inputTimeout = setTimeout(() => {
            runInAction(() => {
              state.isTyping = false;
              state.isLoading = true;
              state.loadError = null;
            });

            makeRequest({
              endpoint: apiEndpoint,
              body: { searchKey: value.trim() },
              onSuccess: (response) => {
                runInAction(() => {
                  state.searchKeyResults = response || [];
                  state.isLoading = false;
                });
              },
              onError: (error) => {
                runInAction(() => {
                  state.loadError = error?.message || 'Failed to load results';
                  state.searchKeyResults = [];
                  state.isLoading = false;
                });
              },
              onFinally: () => {
                runInAction(() => {
                  state.isLoading = false;
                });
              }
            });
          }, debounceMs);
        });
      },

      clearSearchKey: () => {
        runInAction(() => {
          if (state.inputTimeout) {
            clearTimeout(state.inputTimeout);
          }
          state.searchKey = '';
          state.searchKeyResults = [];
          state.isTyping = false;
          state.isLoading = false;
          state.loadError = null;
        });
      },

      get isSearchDisabled() {
        return disabled || state.searchKey.trim().length < minSearchLength || state.isLoading;
      }
    }));

    // TD-1709: Auto-trigger search on mount if initialSearchKey is provided
    useEffect(() => {
      if (initialSearchKey && initialSearchKey.trim().length >= minSearchLength) {
        state.executeSearch();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearchClick = (e) => {
      e?.preventDefault?.();
      state.executeSearch();
    };

    return (
      <div className="profiler-search-by-name-wrapper">
        <ProfilerNameSearchInput
          searchKey={state.searchKey}
          setSearchKey={state.setSearchKey}
          clearSearchKey={state.clearSearchKey}
          disabled={disabled}
          placeholder={placeholder}
          label={label}
          inputId={inputId}
        />
        {showSearchButton && (
          <button
            type="submit"
            className="btn btn-primary"
            disabled={state.isSearchDisabled}
            onClick={handleSearchClick}
          >
            Search
          </button>
        )}
        <ProfilerNameSearchResults
          results={state.searchKeyResults}
          ResultItemComponent={ResultItemComponent}
          resultItemProps={resultItemProps}
          isTyping={state.isTyping}
          isLoading={state.isLoading}
          loadError={state.loadError}
          searchKey={state.searchKey}
          minSearchLength={minSearchLength}
        />
      </div>
    );
  }
);

export default ProfilerNameSearch;
